import React, { useState, useEffect } from 'react';
import routerService from '../services/RouterService';

const RoutersPage = () => {
    const [routers, setRouters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // State para el formulario de nuevo router
    const [newRouter, setNewRouter] = useState({
        nombre_router: '', ip_router: '', api_user: '', api_password: '', api_port: 8728,
    });
    
    // --- NUEVO: State para manejar la edición ---
    const [editingRouter, setEditingRouter] = useState(null);

    const fetchRouters = async () => {
        setLoading(true);
        try {
            const response = await routerService.getRouters();
            setRouters(response.data);
        } catch (err) {
            setError('No se pudo cargar la lista de routers.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRouters();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewRouter(prevState => ({ ...prevState, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await routerService.createRouter(newRouter);
            setNewRouter({ nombre_router: '', ip_router: '', api_user: '', api_password: '', api_port: 8728 });
            fetchRouters();
        } catch (err) {
            setError(err.response?.data?.detail || 'Error al crear el router.');
        }
    };

    // --- NUEVAS FUNCIONES PARA EDITAR Y ELIMINAR ---

    const handleEditClick = (router) => {
        setEditingRouter({ ...router, api_password: '' }); // Ponemos la pass en blanco por seguridad
    };

    const handleUpdateChange = (e) => {
        const { name, value } = e.target;
        setEditingRouter(prevState => ({ ...prevState, [name]: value }));
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        if (!editingRouter) return;
        
        // Creamos un objeto solo con los datos que pueden cambiar
        const updateData = {
            nombre_router: editingRouter.nombre_router,
            ip_router: editingRouter.ip_router,
            api_user: editingRouter.api_user,
            api_port: parseInt(editingRouter.api_port, 10),
        };

        // Solo incluimos la contraseña si el usuario escribió algo
        if (editingRouter.api_password) {
            updateData.api_password = editingRouter.api_password;
        }

        try {
            await routerService.updateRouter(editingRouter.id, updateData);
            setEditingRouter(null);
            fetchRouters();
        } catch (err) {
            setError('Error al actualizar el router.');
        }
    };
    
    const handleDeleteClick = async (routerId) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este router?')) {
            try {
                await routerService.deleteRouter(routerId);
                fetchRouters();
            } catch (err) {
                setError('Error al eliminar el router.');
            }
        }
    };


    return (
        <div className="container mx-auto p-4">
            <h1 className="text-3xl font-bold mb-6 text-gray-800">Gestión de Routers</h1>
            
            {/* Formulario de creación */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h2 className="text-xl font-semibold mb-4">Añadir Nuevo Router</h2>
                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input name="nombre_router" value={newRouter.nombre_router} onChange={handleInputChange} placeholder="Nombre (ej: RB_Principal)" className="p-2 border rounded" required />
                    <input name="ip_router" value={newRouter.ip_router} onChange={handleInputChange} placeholder="Dirección IP" className="p-2 border rounded" required />
                    <input name="api_user" value={newRouter.api_user} onChange={handleInputChange} placeholder="Usuario API" className="p-2 border rounded" required />
                    <input name="api_password" type="password" value={newRouter.api_password} onChange={handleInputChange} placeholder="Contraseña API" className="p-2 border rounded" required />
                    <input name="api_port" type="number" value={newRouter.api_port} onChange={handleInputChange} placeholder="Puerto API" className="p-2 border rounded" required />
                    <button type="submit" disabled={loading} className="md:col-span-2 bg-blue-500 text-white p-2 rounded hover:bg-blue-600 disabled:bg-gray-400">
                        {loading ? 'Guardando...' : 'Guardar Router'}
                    </button>
                </form>
            </div>

            {/* Tabla de Routers */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-4">Routers Configurados</h2>
                {loading && <p>Cargando...</p>}
                {error && <p className="text-red-500">{error}</p>}
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="py-2 px-4 text-left">ID</th>
                                <th className="py-2 px-4 text-left">Nombre</th>
                                <th className="py-2 px-4 text-left">IP</th>
                                <th className="py-2 px-4 text-left">Usuario</th>
                                <th className="py-2 px-4 text-left">Puerto</th>
                                <th className="py-2 px-4 text-left">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {routers.map(router => (
                                <tr key={router.id} className="border-b hover:bg-gray-50">
                                    <td className="py-2 px-4">{router.id}</td>
                                    <td className="py-2 px-4">{router.nombre_router}</td>
                                    <td className="py-2 px-4">{router.ip_router}</td>
                                    <td className="py-2 px-4">{router.api_user}</td>
                                    <td className="py-2 px-4">{router.api_port}</td>
                                    <td className="py-2 px-4">
                                        <button onClick={() => handleEditClick(router)} className="bg-yellow-500 text-white px-3 py-1 rounded text-sm mr-2 hover:bg-yellow-600">Editar</button>
                                        <button onClick={() => handleDeleteClick(router.id)} className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600">Eliminar</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* --- NUEVO: Modal de Edición --- */}
            {editingRouter && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="text-2xl font-semibold mb-4">Editar Router</h2>
                        <form onSubmit={handleUpdateSubmit}>
                            <div className="mb-4">
                                <label className="block text-sm font-bold mb-2">Nombre</label>
                                <input name="nombre_router" value={editingRouter.nombre_router} onChange={handleUpdateChange} className="p-2 border rounded w-full" />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-bold mb-2">Dirección IP</label>
                                <input name="ip_router" value={editingRouter.ip_router} onChange={handleUpdateChange} className="p-2 border rounded w-full" />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-bold mb-2">Usuario API</label>
                                <input name="api_user" value={editingRouter.api_user} onChange={handleUpdateChange} className="p-2 border rounded w-full" />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-bold mb-2">Nueva Contraseña (opcional)</label>
                                <input name="api_password" type="password" value={editingRouter.api_password} onChange={handleUpdateChange} placeholder="Dejar en blanco para no cambiar" className="p-2 border rounded w-full" />
                            </div>
                            <div className="flex justify-end mt-6">
                                <button type="button" onClick={() => setEditingRouter(null)} className="bg-gray-500 text-white py-2 px-4 rounded mr-2 hover:bg-gray-600">Cancelar</button>
                                <button type="submit" className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600">Guardar Cambios</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoutersPage;